import { expVal } from '@atlassian/jira-feature-experiments';
import { createLocalStorageProvider } from '@jira/platform__browser-storage-providers/src/controllers/local-storage/index.tsx';
import { fg } from '@jira/platform__feature-gating';

export type ViewMode = 'MODAL' | 'SIDEBAR' | 'DEFAULT';

/**
 * Which persisted slot a preview-mode preference belongs to.
 *
 * SHARED is the long-standing detail-view preference that board, backlog, the issue navigator,
 * calendar and timeline all read and write, including through `@jira/issue__context-service`.
 * WORK_ITEM is the work item screen's own slot, so switching its preview panel between sidebar and
 * modal cannot change the detail view mode those other surfaces open with.
 */
export type ViewModeStorageScope = 'SHARED' | 'WORK_ITEM';

// The storage keys and provider are private to this package. Reads and writes of a preview-mode
// preference go exclusively through getViewModeFromLocalStorage and setViewModeInLocalStorage, so
// each key string lives in exactly one place and the SHARED key cannot drift between the React
// context package and the @jira/issue__context-service that also persists that preference.
const localStorage = createLocalStorageProvider('jira_issue_view');
const VIEW_MODE_STORAGE_KEY = 'view-mode';
const WORK_ITEM_VIEW_MODE_STORAGE_KEY = 'work-item-view-mode';

// The WORK_ITEM slot only exists while the preview panel expand experiment is on; outside it every
// scope keeps reading and writing the shared key exactly as before.
const getStorageKey = (scope: ViewModeStorageScope): string =>
	scope === 'WORK_ITEM' && expVal('jira_wiv_preview_panel_expand_button', 'isEnabled', false)
		? WORK_ITEM_VIEW_MODE_STORAGE_KEY
		: VIEW_MODE_STORAGE_KEY;

// Plain localStorage read with no SSR guard. When adopting in the issue view context, wrap the call
// in an SSR-safe guard (e.g. useSsrSafeCallback) to avoid a hydration mismatch.
export const getViewModeFromLocalStorage = (scope: ViewModeStorageScope): ViewMode => {
	const stored = localStorage.get(getStorageKey(scope));
	if (stored === 'MODAL' || stored === 'SIDEBAR') {
		return stored;
	}
	return 'DEFAULT';
};

// Written as a strictly necessary item because the preference is functional: a consent-blocked
// set() resolves to status BLOCKED and the provider then removes the key, so the user's choice
// would be silently discarded and every page load would fall back to the default for its storage key.
export const setViewModeInLocalStorage = (
	viewMode: 'MODAL' | 'SIDEBAR',
	scope: ViewModeStorageScope,
): void => {
	const storageKey = getStorageKey(scope);

	if (fg('jira_wiv_view_mode_strictly_necessary_storage')) {
		// @browser-storage setter
		localStorage.setStrictlyNecessaryItem(storageKey, viewMode);
		return;
	}
	// @browser-storage setter
	localStorage.set(storageKey, viewMode);
};
